import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  BarChart3, Loader2, Send, Eye, MousePointerClick, AlertTriangle, Search,
  RefreshCw, PlayCircle, RotateCcw, Users, ChevronDown, ChevronUp, Trash2, MailWarning,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUD_LABELS = { guest: 'Clienti/Ospiti', partner: 'Partner', owner: 'Proprietari', manager: 'Responsabili' };
const STATUS_LABEL = {
  sent: { label: 'Inviata', cls: 'bg-emerald-100 text-emerald-800' },
  sending: { label: 'In invio…', cls: 'bg-blue-100 text-blue-800' },
};

const pct = (num, den) => (den ? `${Math.round((num / den) * 100)}%` : '—');
const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }) : '—';

const KpiBox = ({ label, value, sub, Icon, color }) => (
  <div className="border rounded-lg p-3 text-center bg-white">
    <div className={`text-2xl font-bold ${color || 'text-slate-800'}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center justify-center gap-1 mt-0.5">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export default function NewsletterKpiTab() {
  const token = localStorage.getItem('hw_token');
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [q, setQ] = useState('');
  const [sends, setSends] = useState(null);
  const [failedOpen, setFailedOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [bounces, setBounces] = useState([]);
  const [bounceInfo, setBounceInfo] = useState(null);
  const [manualEmail, setManualEmail] = useState('');

  const loadBounces = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/newsletters/bounces`, H);
      setBounces(res.data.bounces || []);
      setBounceInfo(res.data.info || null);
    } catch (_) { /* silente */ }
  }, []); // eslint-disable-line

  useEffect(() => { loadBounces(); }, [loadBounces]);

  const scanBounces = async () => {
    setBusy('bounce');
    try {
      const res = await axios.post(`${API}/newsletters/bounces/scan`, {}, H);
      toast.success(`Casella controllata (${res.data.messages_checked} messaggi): ${res.data.new_bounces} nuovi rimbalzi, ${res.data.deactivated} contatti disattivati`);
      loadBounces();
      if (selId) openStats(selId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore scansione casella'); }
    finally { setBusy(''); }
  };

  const suppressManualEmail = async () => {
    const email = manualEmail.trim().toLowerCase();
    if (!email) return;
    setBusy('manual-suppression');
    try {
      await axios.post(`${API}/newsletters/suppressions`, {
        email, reason: 'rinuncia volontaria richiesta dal destinatario'
      }, H);
      toast.success('Indirizzo escluso da tutti gli invii automatici');
      setManualEmail('');
      loadBounces();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile escludere l’indirizzo');
    } finally { setBusy(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletters`, H);
      setItems((res.data.newsletters || []).filter((n) => ['sent', 'sending'].includes(n.status)));
    } catch (_) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openStats = async (id) => {
    setSelId(id); setStats(null); setSends(null); setQ(''); setFailedOpen(false);
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/newsletters/${id}/stats`, H);
      setStats(res.data);
    } catch (e) { toast.error('Errore statistiche'); }
    finally { setStatsLoading(false); }
  };

  const searchSends = async () => {
    try {
      const res = await axios.get(`${API}/newsletters/${selId}/sends?q=${encodeURIComponent(q)}`, H);
      setSends(res.data.sends || []);
    } catch (_) { toast.error('Errore ricerca'); }
  };

  const cleanupFailed = async () => {
    if (!window.confirm('Eliminare gli invii falliti e disattivare i contatti ospiti con email errata?\nNon riceveranno più nessuna newsletter futura.')) return;
    setBusy('cleanup');
    try {
      const res = await axios.post(`${API}/newsletters/${selId}/failed/cleanup`, {}, H);
      toast.success(`Pulizia completata: ${res.data.suppressed} indirizzi soppressi; ${res.data.temporary_kept} errori temporanei conservati`);
      openStats(selId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore pulizia'); }
    finally { setBusy(''); }
  };

  const resume = async (retryFailed) => {
    const msg = retryFailed
      ? 'Rimettere in coda gli invii FALLITI e riprovarli adesso?'
      : 'Riprendere adesso gli invii rimasti in coda?';
    if (!window.confirm(msg)) return;
    setBusy(retryFailed ? 'retry' : 'resume');
    try {
      const res = await axios.post(`${API}/newsletters/${selId}/resume`, { retry_failed: retryFailed }, H);
      toast.success(`Ripresa invio: ${res.data.queued} email in coda${res.data.real_send ? '' : ' (SIMULATO in preview)'}`);
      setTimeout(() => openStats(selId), 1500);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setBusy(''); }
  };

  const t = stats?.totals;
  const nlInfo = stats?.newsletter;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="newsletter-kpi-tab">
      <div className="space-y-2">
        {loading ? (
          <Card className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></Card>
        ) : items.length === 0 ? (
          <Card className="p-6 text-sm text-slate-500 text-center">Nessuna newsletter inviata finora</Card>
        ) : items.map((nl) => {
          const st = STATUS_LABEL[nl.status] || STATUS_LABEL.sent;
          const s = nl.stats || {};
          return (
            <Card key={nl.id}
              className={`p-3 cursor-pointer hover:border-blue-300 ${selId === nl.id ? 'border-blue-500 ring-1 ring-blue-200' : ''}`}
              onClick={() => openStats(nl.id)} data-testid={`kpi-nl-row-${nl.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">{nl.property_name}</p>
                <Badge className={`${st.cls} text-[10px]`}>{st.label}</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{fmtDt(nl.sent_at || nl.approved_at)}
                {nl.simulated && <Badge variant="outline" className="ml-2 text-[9px]">SIMULATA</Badge>}
              </p>
              <p className="text-xs text-slate-600 mt-1 flex items-center gap-3">
                <span><Users className="h-3 w-3 inline" /> {s.recipients ?? '—'}</span>
                <span><Send className="h-3 w-3 inline" /> {s.sent ?? 0}</span>
                <span><Eye className="h-3 w-3 inline" /> {s.opens ?? 0}</span>
                <span><MousePointerClick className="h-3 w-3 inline" /> {s.clicks ?? 0}</span>
                {(s.failed || 0) > 0 && <span className="text-red-600 font-semibold">✗ {s.failed}</span>}
              </p>
            </Card>
          );
        })}
      </div>

      <div className="lg:col-span-2">
        {!selId ? (
          <Card className="p-10 text-center text-sm text-slate-400">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            Seleziona una newsletter per vedere i KPI
          </Card>
        ) : statsLoading || !stats ? (
          <Card className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></Card>
        ) : (
          <Card className="p-4 space-y-4" data-testid="kpi-detail">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-bold text-slate-800">{nlInfo.property_name}
                  {nlInfo.simulated && <Badge variant="outline" className="ml-2 text-[10px]">INVIO SIMULATO (preview)</Badge>}
                </p>
                <p className="text-xs text-slate-500">
                  Oggetto: {nlInfo.subject} · Inviata {fmtDt(nlInfo.sent_at || nlInfo.approved_at)}
                  {nlInfo.approved_by ? ` da ${nlInfo.approved_by}` : ''}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => openStats(selId)} data-testid="kpi-refresh-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="kpi-totals">
              <KpiBox label="Destinatari" value={t.total} Icon={Users} />
              <KpiBox label="Inviate" value={t.sent} sub={pct(t.sent, t.total)} Icon={Send} color="text-emerald-600" />
              <KpiBox label="Fallite" value={t.failed} Icon={AlertTriangle} color={t.failed ? 'text-red-600' : 'text-slate-300'} />
              <KpiBox label="In coda" value={t.queued} color={t.queued ? 'text-amber-600' : 'text-slate-300'} />
              <KpiBox label="Aperture" value={t.opens} sub={pct(t.opens, t.sent)} Icon={Eye} color="text-blue-700" />
              <KpiBox label="Click" value={t.clicks} sub={pct(t.clicks, t.sent)} Icon={MousePointerClick} color="text-indigo-700" />
            </div>

            {(t.queued > 0 || t.failed > 0) && (
              <div className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-xs text-amber-800 font-medium">
                  {t.queued > 0 && `${t.queued} email mai partite (in coda)`}
                  {t.queued > 0 && t.failed > 0 && ' · '}
                  {t.failed > 0 && `${t.failed} fallite`}
                </span>
                <div className="ml-auto flex gap-2">
                  {t.queued > 0 && (
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => resume(false)} disabled={!!busy} data-testid="kpi-resume-btn">
                      {busy === 'resume' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><PlayCircle className="h-3 w-3 mr-1" /> Riprendi invii</>}
                    </Button>
                  )}
                  {t.failed > 0 && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => resume(true)} disabled={!!busy} data-testid="kpi-retry-failed-btn">
                      {busy === 'retry' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" /> Riprova fallite</>}
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Per pubblico</p>
              <table className="w-full text-sm" data-testid="kpi-audience-table">
                <thead>
                  <tr className="text-[11px] text-slate-400 uppercase text-left">
                    <th className="py-1">Pubblico</th><th className="text-center">Destinatari</th>
                    <th className="text-center">Inviate</th><th className="text-center">Fallite</th>
                    <th className="text-center">In coda</th><th className="text-center">Aperture</th>
                    <th className="text-center">Click</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(stats.by_audience).map(([a, d]) => (
                    <tr key={a} className="border-t">
                      <td className="py-1.5 font-medium">{AUD_LABELS[a] || a}</td>
                      <td className="text-center">{d.total}</td>
                      <td className="text-center text-emerald-700">{d.sent}</td>
                      <td className={`text-center ${d.failed ? 'text-red-600 font-semibold' : 'text-slate-300'}`}>{d.failed}</td>
                      <td className={`text-center ${d.queued ? 'text-amber-600 font-semibold' : 'text-slate-300'}`}>{d.queued}</td>
                      <td className="text-center">{d.opens} <span className="text-[10px] text-slate-400">({pct(d.opens, d.sent)})</span></td>
                      <td className="text-center">{d.clicks} <span className="text-[10px] text-slate-400">({pct(d.clicks, d.sent)})</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {Object.keys(stats.by_lang || {}).length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] text-slate-400 uppercase mr-1">Lingue ospiti:</span>
                {Object.entries(stats.by_lang).map(([l, n]) => (
                  <span key={l} className="text-[11px] bg-slate-100 rounded px-2 py-0.5 font-medium uppercase">{l}: {n}</span>
                ))}
              </div>
            )}

            {stats.failed_list?.length > 0 && (
              <div>
                <div className="flex items-center gap-2">
                  <button className="text-xs font-semibold text-red-600 flex items-center gap-1"
                    onClick={() => setFailedOpen((v) => !v)} data-testid="kpi-failed-toggle">
                    <AlertTriangle className="h-3 w-3" /> Email fallite ({t.failed})
                    {failedOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                    onClick={cleanupFailed} disabled={busy === 'cleanup'} data-testid="kpi-cleanup-failed-btn"
                    title="Elimina i record falliti e disattiva i contatti ospiti con email errata (esclusi dalle prossime newsletter)">
                    {busy === 'cleanup' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                    Sopprimi definitive
                  </Button>
                </div>
                {failedOpen && (
                  <div className="mt-1 max-h-40 overflow-y-auto border rounded-md divide-y">
                    {stats.failed_list.map((f, i) => (
                      <div key={i} className="px-2 py-1 text-xs flex justify-between gap-2">
                        <span className="font-mono">{f.email} <span className="text-slate-400">({AUD_LABELS[f.audience] || f.audience})</span></span>
                        <span className="text-red-500 truncate max-w-[50%]">{f.error}</span>
                      </div>
                    ))}
                    {t.failed > stats.failed_list.length && (
                      <div className="px-2 py-1 text-[11px] text-slate-400">
                        …mostrate le prime {stats.failed_list.length} di {t.failed}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Verifica destinatario — "l'ha ricevuta?"
              </p>
              <div className="flex gap-2">
                <Input className="h-8 text-sm" placeholder="cerca email (anche parziale)…" value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchSends()}
                  data-testid="kpi-search-input" />
                <Button variant="outline" size="sm" onClick={searchSends} data-testid="kpi-search-btn">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              {sends && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-md divide-y" data-testid="kpi-search-results">
                  {sends.length === 0 ? (
                    <p className="px-2 py-2 text-xs text-slate-500">
                      Nessun invio trovato per questa ricerca: il destinatario NON era in lista (verifica email in rubrica/partner)
                    </p>
                  ) : sends.map((s, i) => (
                    <div key={i} className="px-2 py-1.5 text-xs flex items-center gap-2 flex-wrap">
                      <span className="font-mono">{s.email}</span>
                      <Badge variant="outline" className="text-[9px]">{AUD_LABELS[s.audience] || s.audience}</Badge>
                      {s.status === 'sent' && <span className="text-emerald-600 font-semibold">✓ inviata {fmtDt(s.sent_at)}</span>}
                      {s.status === 'failed' && <span className="text-red-600 font-semibold">✗ fallita {s.error ? `(${s.error.slice(0, 60)})` : ''}</span>}
                      {['queued', 'processing'].includes(s.status) && <span className="text-amber-600 font-semibold">⏳ in coda (mai partita)</span>}
                      {s.opened_at && <span className="text-blue-600">👁 aperta</span>}
                      {s.clicked_at && <span className="text-indigo-600">🖱 click</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      <div className="lg:col-span-3">
        <Card className="p-4" data-testid="bounce-tracker-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-semibold text-sm flex items-center gap-2">
                <MailWarning className="h-4 w-4 text-amber-600" /> Rimbalzi post-invio (bounce)
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Controllo automatico ogni 2 ore: gli indirizzi definitivamente inesistenti vengono esclusi da tutti gli invii automatici; quota, casella piena e blocchi temporanei non vengono soppressi.
                {bounceInfo?.last_scan_at && <> · Ultimo controllo: {fmtDt(bounceInfo.last_scan_at)}</>}
                {Number.isInteger(bounceInfo?.global_suppressions) && <span data-testid="global-email-suppressions-count"> · Soppressioni globali: {bounceInfo.global_suppressions}</span>}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={scanBounces} disabled={busy === 'bounce'} data-testid="bounce-scan-btn">
              {busy === 'bounce' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Controlla ora
            </Button>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="manual-email-suppression-form">
            <Input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)}
              placeholder="Email da escludere su richiesta del destinatario"
              data-testid="manual-email-suppression-input" />
            <Button variant="outline" onClick={suppressManualEmail}
              disabled={!manualEmail.trim() || busy === 'manual-suppression'}
              data-testid="manual-email-suppression-button">
              {busy === 'manual-suppression' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailWarning className="h-4 w-4 mr-1" />}
              Escludi indirizzo
            </Button>
          </div>
          {bounces.length > 0 ? (
            <div className="mt-3 max-h-56 overflow-y-auto border rounded-md divide-y" data-testid="bounce-list">
              {bounces.map((b) => (
                <div key={b.id} className="px-2 py-1.5 text-xs flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{b.email}</span>
                  <Badge className={`text-[9px] ${b.hard ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {b.hard ? 'DEFINITIVO' : 'TEMPORANEO'}
                  </Badge>
                  {b.contact_deactivated && <Badge variant="outline" className="text-[9px] text-red-600 border-red-200">contatto disattivato</Badge>}
                  <span className="text-slate-400 truncate max-w-[40%]" title={b.reason}>{b.reason}</span>
                  <span className="ml-auto text-slate-400">{fmtDt(b.processed_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-400">Nessun rimbalzo rilevato finora</p>
          )}
        </Card>
      </div>
    </div>
  );
}
